import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Typography
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { ViewIconStroke, ViewOffSlashIconStroke } from '@/components/icons';

import { T } from '../../util/Localize';
import { TextField } from '../../components/input';
import Api from '../../util/Api';
import { HUB_FORGOT_PASSWORD_PATH, HUB_LOGIN_PATH } from '../../routes/paths';
import { HttpError } from '../../services/http';
import {
    checkPasswordResetStatus,
    setPassword,
    setPasswordFieldError
} from '../../services/password-reset';
import { isBrandSuspendedError } from '../../services/session-refusal';
import { SuspendedAccountNotice } from '../../components/errors/suspended-account-notice';
import { AuthPageLayout } from './auth-page-layout';
import { authSubmitButtonSx } from './auth-submit-button';
import { postLoginDestination } from './post-login-destination';

const MIN_PASSWORD_LENGTH = 10;
// proc_ResetPassword's varchar(32); the API refuses anything longer (API-520).
const MAX_PASSWORD_LENGTH = 32;

// 'suspended': the business is suspended (403 BRAND_SUSPENDED). 'passwordSet': saved, but
// sign-in failed (500).
type LinkStatus = 'checking' | 'valid' | 'invalid' | 'suspended' | 'passwordSet';

export default function SetPassword() {
    const [searchParams] = useSearchParams();
    const guid = searchParams.get('id');
    const [status, setStatus] = useState<LinkStatus>('checking');
    const [invalidLinkEmail, setInvalidLinkEmail] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!guid) {
            setStatus('invalid');
            return;
        }
        let cancelled = false;
        checkPasswordResetStatus(guid)
            .then(result => {
                if (cancelled) return;
                if (result.valid) {
                    setStatus('valid');
                } else {
                    setInvalidLinkEmail(result.email);
                    setStatus('invalid');
                }
            })
            .catch(() => {
                // Fail closed: an unreadable status (network error, endpoint not yet built —
                // see checkPasswordResetStatus's doc comment) means the link can't be trusted.
                if (!cancelled) setStatus('invalid');
            });
        return () => {
            cancelled = true;
        };
    }, [guid]);

    return (
        <AuthPageLayout>
            {status === 'checking' ? (
                <Box display="flex" justifyContent="center" py={8}>
                    <CircularProgress color="inherit" size={28} sx={{ opacity: 0.6 }} />
                </Box>
            ) : status === 'invalid' ? (
                <InvalidLinkScreen email={invalidLinkEmail} />
            ) : status === 'suspended' ? (
                <AccountSuspendedScreen />
            ) : status === 'passwordSet' ? (
                <PasswordSetScreen />
            ) : (
                <SetPasswordForm
                    guid={guid as string}
                    onLinkInvalid={() => {
                        // A 404 on redeem: no email to prefill, whatever the status check said.
                        setInvalidLinkEmail(undefined);
                        setStatus('invalid');
                    }}
                    onAccountSuspended={() => setStatus('suspended')}
                    onPasswordSet={() => setStatus('passwordSet')}
                />
            )}
        </AuthPageLayout>
    );
}

function SetPasswordForm({
    guid,
    onLinkInvalid,
    onAccountSuspended,
    onPasswordSet
}: {
    guid: string;
    onLinkInvalid: () => void;
    onAccountSuspended: () => void;
    onPasswordSet: () => void;
}) {
    const [password, setPasswordValue] = useState('');
    const [touched, setTouched] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);

    const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
    // Shown as soon as it's over, touched or not: typing on can never bring it back under.
    const tooLong = password.length > MAX_PASSWORD_LENGTH;
    const canSubmit = password.length >= MIN_PASSWORD_LENGTH && !tooLong;

    const fieldError = tooLong
        ? T('Password must be 32 characters or fewer.')
        : touched && tooShort
          ? T('Password must be at least 10 characters.')
          : passwordError;

    const handleSubmit = async () => {
        setTouched(true);
        if (!canSubmit || submitting) return;
        setSubmitting(true);
        setError(null);
        setPasswordError(null);
        try {
            const auth = await setPassword(guid, password);
            Api.sessionUpdate(auth);
            // `Api.user`, as Login.tsx reads it: `Api.sessionUpdate` has just built the user from
            // this response, so a suspended business's Owner (`suspended: true`) lands on billing.
            window.location.assign(postLoginDestination(Api.user));
        } catch (err) {
            if (err instanceof HttpError && err.status === 404) {
                onLinkInvalid();
                return;
            }
            // ROBO-478: only the BRAND_SUSPENDED code is a suspension. A 403 without it is not,
            // and falls through to the generic message below.
            if (isBrandSuspendedError(err)) {
                onAccountSuspended();
                return;
            }
            if (err instanceof HttpError && err.status === 500) {
                onPasswordSet();
                return;
            }
            // A 400 naming the password shows on the field; anything else is the generic message.
            const message = setPasswordFieldError(err);
            if (message) {
                setPasswordError(message);
            } else {
                setError('Something went wrong. Please try again.');
            }
            setSubmitting(false);
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={4}>
            <Box>
                <Typography sx={{ color: 'inherit', fontWeight: 900, fontSize: 36, lineHeight: 1 }}>
                    {T('Set your password')}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1 }}>
                    {T(
                        "Choose a password you'll remember and you're in. Just needs to be at least 10 characters."
                    )}
                </Typography>
            </Box>
            <Box
                component="form"
                display="flex"
                flexDirection="column"
                gap={1.25}
                onSubmit={(e: React.FormEvent) => {
                    e.preventDefault();
                    handleSubmit();
                }}
            >
                <TextField
                    type={showPassword ? 'text' : 'password'}
                    label={T('Password')}
                    value={password}
                    disabled={submitting}
                    error={Boolean(fieldError)}
                    helperText={fieldError || undefined}
                    onChange={(e: any) => {
                        setPasswordValue(e.target.value);
                        setPasswordError(null);
                    }}
                    onBlur={() => setTouched(true)}
                    labelSx={{ color: 'inherit' }}
                    slotProps={{
                        inputLabel: { shrink: true },
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? (
                                            <HugeiconsIcon
                                                icon={ViewOffSlashIconStroke}
                                                strokeWidth={1.5}
                                            />
                                        ) : (
                                            <HugeiconsIcon
                                                icon={ViewIconStroke}
                                                strokeWidth={1.5}
                                            />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }
                    }}
                />
                {error && (
                    <Typography sx={{ color: '#ff8a80', fontSize: 13 }}>{T(error)}</Typography>
                )}
                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={!canSubmit || submitting}
                    sx={authSubmitButtonSx}
                >
                    {submitting ? (
                        <CircularProgress size={22} sx={{ color: 'inherit' }} />
                    ) : (
                        T('Save Password')
                    )}
                </Button>
            </Box>
        </Box>
    );
}

function InvalidLinkScreen({ email }: { email?: string }) {
    return (
        <Box display="flex" flexDirection="column" gap={4}>
            <Box>
                <Typography sx={{ color: 'inherit', fontWeight: 900, fontSize: 36, lineHeight: 1 }}>
                    {T("Let's get you a new link")}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1 }}>
                    {T(
                        'This one either expired or has already been used. Password links last 48 hours and work a single time.'
                    )}
                </Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={1.25}>
                <Button
                    component={RouterLink}
                    to={HUB_FORGOT_PASSWORD_PATH}
                    state={email ? { email } : undefined}
                    variant="contained"
                    fullWidth
                    sx={{ py: 1 }}
                >
                    {T('Request a New Link')}
                </Button>
                <Box display="flex" gap={0.75} justifyContent="center" sx={{ fontSize: 14 }}>
                    <Typography sx={{ color: 'inherit', fontSize: 'inherit' }}>
                        {T('Already set your password?')}
                    </Typography>
                    <RouterLink to={HUB_LOGIN_PATH} style={{ color: 'inherit', fontWeight: 700 }}>
                        {T('Sign in')}
                    </RouterLink>
                </Box>
            </Box>
        </Box>
    );
}

/**
 * POST 403 BRAND_SUSPENDED (ROBO-478): the link is valid, but the user is a non-Owner of a suspended
 * business (its own suspension or its parent's). Nothing was changed. The shared notice is the one
 * the login screen shows for the same refusal and carries its own "Account Inactive" title, so no
 * page heading competes with it.
 */
function AccountSuspendedScreen() {
    return (
        <Box display="flex" flexDirection="column" gap={4}>
            {/* No top margin: the panel's padding already spaces the top, as for the headings on
                the other end-screens. */}
            <SuspendedAccountNotice sx={{ mt: 0 }} />
            <BackToSignInButton />
        </Box>
    );
}

/** POST 500: the password WAS set, but signing in failed. */
function PasswordSetScreen() {
    return (
        <Box display="flex" flexDirection="column" gap={4}>
            <Box>
                <Typography sx={{ color: 'inherit', fontWeight: 900, fontSize: 36, lineHeight: 1 }}>
                    {T('Your password has been set')}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1 }}>
                    {T(
                        "We couldn't sign you in automatically. Please sign in with your new password."
                    )}
                </Typography>
            </Box>
            <BackToSignInButton />
        </Box>
    );
}

/** The end-screen exit, matching PasswordResetRequest's PasswordResetSent. */
function BackToSignInButton() {
    return (
        <Button
            component={RouterLink}
            to={HUB_LOGIN_PATH}
            variant="contained"
            fullWidth
            sx={{ py: 1 }}
        >
            {T('Back to Sign in')}
        </Button>
    );
}
